import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import type { AppError } from '../../api/contract';
import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { countActiveFilterValues } from '../../shared/filter/activeFilterCount';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { reportExportsRepositoryToken } from '../report-exports/repository';
import { compatibleReportTimeBasis, normalizeFiltersForKind, reportDefinition } from './definition';
import { reportFilterLabel, reportKindLabel } from './labels';
import { reportsRepositoryToken } from './repository';
import { ReportSummary } from './ReportSummary';
import {
  emptyReportFilters,
  type ReportFilters,
  type ReportKind,
  type ReportQuery,
  type ReportRow,
} from './types';

export const reportKinds = [
  'transactions',
  'agent-commissions',
  'affiliate-commissions',
  'games',
  'registrations',
  'partner-reconciliation',
  'commission-rule-usage',
  'audit-activity',
  'wallets',
  'wallet-ledger',
  'withdrawals',
  'partner-credit-reconciliation',
  'user-affiliate-by-game',
] as const satisfies readonly ReportKind[];

const reportKindSet = new Set<string>(reportKinds);
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
const timestampPattern =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/u;
const sortSet = new Set(['occurredAt', 'amount', 'code', 'name']);
const stringFields = [
  'status',
  'outcome',
  'code',
  'username',
  'partnerUserId',
  'entryType',
] as const;
const uuidFields = [
  'partnerId',
  'agentId',
  'memberId',
  'uplineMemberId',
  'gameTypeId',
  'gameId',
] as const;

export function ReportPage({ dataSource }: { readonly dataSource: DataSource }) {
  const reports = useRepository(reportsRepositoryToken);
  const exportsRepository = useRepository(reportExportsRepositoryToken);
  const auth = useAuth();
  const { reportKind: rawKind } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const kind: ReportKind = isReportKind(rawKind) ? rawKind : 'transactions';
  const principal = auth.status === 'authenticated' ? auth.principal : null;
  const [query, setQuery] = useState<ReportQuery>(() =>
    initialQuery(
      kind,
      searchParams,
      principal?.scope.partnerId ?? null,
      principal?.scope.agentId ?? null,
    ),
  );
  const [draft, setDraft] = useState(query.filters);
  const [history, setHistory] = useState<readonly string[]>(['']);
  const [page, setPage] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [exportError, setExportError] = useState<AppError | null>(null);
  const exportIntent = useRef<{ signature: string; key: string } | null>(null);
  const submitting = useRef(false);
  const resource = useResource(
    (signal) => reports.run(kind, query, signal),
    [kind, query, refreshKey, reports],
    (result) => result.rows.length === 0 && query.cursor === '',
  );
  const reportTimezone = resource.data?.meta.timezone ?? 'Asia/Bangkok';
  const canExport =
    (principal?.permissions.includes('REPORT_VIEW') ?? false) &&
    (principal?.permissions.includes('REPORT_EXPORT') ?? false);
  const defaultFilters = normalizeFilters(
    kind,
    emptyReportFilters,
    principal?.scope.partnerId ?? null,
    principal?.scope.agentId ?? null,
  );
  const columns = useMemo<readonly GridColDef<ReportRow>[]>(
    () => [
      {
        field: 'occurredAt',
        headerName: t('reports.columns.occurredAt'),
        minWidth: 190,
        flex: 1,
        valueFormatter: (value: string) => formatTimestamp(value, reportTimezone, language).display,
      },
      {
        field: 'identity',
        headerName: t('reports.columns.identity'),
        minWidth: 220,
        flex: 1,
        valueGetter: (_value, row) => row.code ?? row.username ?? row.transactionId ?? row.id,
      },
      { field: 'status', headerName: t('reports.columns.status'), minWidth: 130 },
      { field: 'currencyCode', headerName: t('reports.columns.currency'), minWidth: 110 },
      {
        field: 'metrics',
        headerName: t('reports.columns.metrics'),
        minWidth: 320,
        flex: 2,
        sortable: false,
        valueFormatter: (value: Readonly<Record<string, unknown>>) => formatScalars(value),
      },
    ],
    [language, reportTimezone, t],
  );

  const apply = (event: FormEvent) => {
    event.preventDefault();
    const filters = normalizeFilters(
      kind,
      draft,
      principal?.scope.partnerId ?? null,
      principal?.scope.agentId ?? null,
    );
    const nextParams = filtersToSearchParams(kind, filters);
    setSearchParams(nextParams, { replace: true });
    setQuery({ filters, cursor: '', limit: query.limit });
    setHistory(['']);
    setPage(0);
    setExportMessage(null);
    setExportError(null);
  };
  const clear = () => {
    const filters = defaultFilters;
    setDraft(filters);
    setSearchParams(filtersToSearchParams(kind, filters), { replace: true });
    setQuery({ filters, cursor: '', limit: query.limit });
    setHistory(['']);
    setPage(0);
    setExportMessage(null);
    setExportError(null);
  };
  const next = () => {
    const cursor = resource.data?.nextCursor;
    if (cursor === null || cursor === undefined) return;
    const index = page + 1;
    setHistory((current) => [...current.slice(0, index), cursor]);
    setPage(index);
    setQuery((current) => ({ ...current, cursor }));
  };
  const previous = () => {
    if (page === 0) return;
    const index = page - 1;
    setPage(index);
    setQuery((current) => ({ ...current, cursor: history[index] ?? '' }));
  };
  const createExport = async () => {
    if (!canExport || submitting.current) return;
    const filters = normalizeFiltersForKind(kind, query.filters);
    const signature = JSON.stringify({ kind, filters });
    if (exportIntent.current?.signature !== signature)
      exportIntent.current = { signature, key: crypto.randomUUID() };
    submitting.current = true;
    setExportError(null);
    try {
      const result = await exportsRepository.create(
        { reportType: kind, filters },
        exportIntent.current.key,
      );
      setExportMessage(t('reports.exportQueued', { id: result.data.id }));
      exportIntent.current = null;
    } catch (error) {
      setExportError(error as AppError);
    } finally {
      submitting.current = false;
    }
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={
          <Stack direction="row" spacing={1}>
            <DataSourceBadge source={dataSource} />
            {canExport ? (
              <Button onClick={() => void createExport()} variant="contained">
                {t('actions.export')}
              </Button>
            ) : null}
            {canExport ? (
              <Button component={Link} to={paths.reportExports}>
                {t('nav.reportExports')}
              </Button>
            ) : null}
          </Stack>
        }
        subtitle={t('reports.subtitle')}
        title={t('reports.title')}
      />
      <ReportFilterPanel
        agentLocked={principal?.scope.agentId !== null && principal?.scope.agentId !== undefined}
        appliedFilters={query.filters}
        draft={draft}
        kind={kind}
        defaultFilters={defaultFilters}
        onChange={setDraft}
        onClear={clear}
        onKindChange={(nextKind) => {
          const filters = normalizeFilters(
            nextKind,
            draft,
            principal?.scope.partnerId ?? null,
            principal?.scope.agentId ?? null,
          );
          setDraft(filters);
          setQuery({ filters, cursor: '', limit: query.limit });
          setHistory(['']);
          setPage(0);
          setExportMessage(null);
          setExportError(null);
          navigate(`/reports/${nextKind}?${filtersToSearchParams(nextKind, filters).toString()}`);
        }}
        onSubmit={apply}
        partnerLocked={
          principal?.scope.partnerId !== null && principal?.scope.partnerId !== undefined
        }
      />
      {exportMessage === null ? null : (
        <Alert severity="success" sx={{ mb: 2 }}>
          {exportMessage}
        </Alert>
      )}
      {exportError === null ? null : (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('reports.exportError')}
          {exportError.requestId === null
            ? ''
            : ` · ${t('reports.requestId', { id: exportError.requestId })}`}
        </Alert>
      )}
      <PageState
        emptyMessage={t('reports.empty')}
        error={safeError(resource.error, t('errors.generic'))}
        onRetry={() => setRefreshKey((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={1.5}>
            {resource.data.meta.stale ? (
              <Alert severity="warning">{t('reports.stale')}</Alert>
            ) : null}
            <Typography color="text.secondary" variant="body2">
              {t('reports.snapshot', {
                id: resource.data.meta.snapshotId,
                date: formatTimestamp(
                  resource.data.meta.reportingAsOf,
                  resource.data.meta.timezone,
                  language,
                ).display,
              })}
            </Typography>
            <ReportSummary
              language={language}
              meta={resource.data.meta}
              totals={resource.data.totals}
            />
            {mobile ? (
              <Stack
                aria-label={t('reports.title')}
                component="ul"
                spacing={1}
                sx={{ listStyle: 'none', m: 0, p: 0 }}
              >
                {resource.data.rows.map((row) => (
                  <Paper component="li" key={row.id} sx={{ p: 2 }} variant="outlined">
                    <Stack spacing={0.5}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {row.code ?? row.username ?? row.transactionId ?? row.id}
                      </Typography>
                      <Typography>
                        {row.status ?? t('common.notAvailable')} ·{' '}
                        {row.currencyCode ?? t('common.notAvailable')}
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        {formatTimestamp(row.occurredAt, reportTimezone, language).display}
                      </Typography>
                      <Typography variant="caption">{formatScalars(row.metrics)}</Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Box
                aria-label={t('reports.title')}
                role="region"
                sx={{ overflowX: 'auto' }}
                tabIndex={0}
              >
                <Box sx={{ height: 440, minWidth: 1000 }}>
                  <DataGrid
                    columns={columns}
                    disableRowSelectionOnClick
                    hideFooter
                    rows={resource.data.rows}
                    sortingMode="server"
                  />
                </Box>
              </Box>
            )}
            <Typography color="text.secondary" variant="caption">
              {t('reports.pagination.server')}
            </Typography>
            {resource.data.requestId === null ? null : (
              <Typography variant="caption">
                {t('reports.requestId', { id: resource.data.requestId })}
              </Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
              <Button disabled={page === 0} onClick={previous}>
                {t('reports.pagination.previous')}
              </Button>
              <Button disabled={resource.data.nextCursor === null} onClick={next}>
                {t('reports.pagination.next')}
              </Button>
            </Stack>
          </Stack>
        )}
      </PageState>
    </Box>
  );
}

function ReportFilterPanel({
  agentLocked,
  appliedFilters,
  draft,
  defaultFilters,
  kind,
  onChange,
  onClear,
  onKindChange,
  onSubmit,
  partnerLocked,
}: {
  readonly agentLocked: boolean;
  readonly appliedFilters: ReportFilters;
  readonly draft: ReportFilters;
  readonly defaultFilters: ReportFilters;
  readonly kind: ReportKind;
  readonly onChange: (filters: ReportFilters) => void;
  readonly onClear: () => void;
  readonly onKindChange: (kind: ReportKind) => void;
  readonly onSubmit: (event: FormEvent) => void;
  readonly partnerLocked: boolean;
}) {
  const { t } = useTranslation();
  const definition = reportDefinition(kind);
  const { primaryKeys, secondaryKeys } = reportFilterLayout(definition.filterKeys);
  const set = (field: keyof ReportFilters, value: string) => onChange({ ...draft, [field]: value });
  const renderFilter = (field: keyof ReportFilters) => {
    if (field === 'timeBasis')
      return (
        <TextField
          key={field}
          label={reportFilterLabel(field, t)}
          onChange={(event) => set(field, event.target.value)}
          select
          size="small"
          value={compatibleReportTimeBasis(kind, draft.timeBasis)}
        >
          {definition.allowedTimeBases.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`reports.timeBases.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      );
    if (field === 'sort')
      return (
        <TextField
          key={field}
          label={reportFilterLabel(field, t)}
          onChange={(event) => set(field, event.target.value)}
          select
          size="small"
          value={draft.sort}
        >
          {['occurredAt', 'amount', 'code', 'name'].map((value) => (
            <MenuItem key={value} value={value}>
              {t(`reports.sortOptions.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      );
    return (
      <TextField
        disabled={(field === 'partnerId' && partnerLocked) || (field === 'agentId' && agentLocked)}
        key={field}
        label={reportFilterLabel(field, t)}
        onChange={(event) => set(field, event.target.value)}
        size="small"
        value={draft[field]}
      />
    );
  };
  return (
    <ListFilterPanel
      activeSecondaryFilterCount={countActiveFilterValues(
        Object.fromEntries(secondaryKeys.map((field) => [field, appliedFilters[field]])),
        Object.fromEntries(secondaryKeys.map((field) => [field, defaultFilters[field]])),
      )}
      ariaLabel={t('filterPanel.label', { page: t('nav.reports') })}
      onClear={onClear}
      onSubmit={onSubmit}
      primaryFields={
        <>
          <TextField
            label={t('reports.type')}
            onChange={(event) => onKindChange(event.target.value as ReportKind)}
            select
            size="small"
            value={kind}
          >
            {reportKinds.map((value) => (
              <MenuItem key={value} value={value}>
                {reportKindLabel(value, t)}
              </MenuItem>
            ))}
          </TextField>
          {primaryKeys.map(renderFilter)}
        </>
      }
      secondaryFields={
        secondaryKeys.length === 0 ? undefined : <>{secondaryKeys.map(renderFilter)}</>
      }
    />
  );
}

export function reportFilterLayout(filterKeys: readonly (keyof ReportFilters)[]): {
  readonly primaryKeys: readonly (keyof ReportFilters)[];
  readonly secondaryKeys: readonly (keyof ReportFilters)[];
} {
  const hasSecondary = filterKeys.length + 1 >= 7;
  return {
    primaryKeys: hasSecondary ? filterKeys.slice(0, 3) : filterKeys,
    secondaryKeys: hasSecondary ? filterKeys.slice(3) : [],
  };
}

function initialQuery(
  kind: ReportKind,
  params: URLSearchParams,
  partnerScope: string | null,
  agentScope: string | null,
): ReportQuery {
  const fromParams = {
    ...emptyReportFilters,
    ...Object.fromEntries(
      reportDefinition(kind).filterKeys.map((field) => [field, params.get(field)?.trim() ?? '']),
    ),
  } as ReportFilters;
  return {
    filters: normalizeFilters(kind, fromParams, partnerScope, agentScope),
    cursor: '',
    limit: 50,
  };
}

function normalizeFilters(
  kind: ReportKind,
  value: ReportFilters,
  partnerScope: string | null,
  agentScope: string | null,
): ReportFilters {
  const definition = reportDefinition(kind);
  return {
    ...emptyReportFilters,
    ...Object.fromEntries(
      definition.filterKeys.map((field) => [
        field,
        sanitizedFilterValue(kind, field, value[field]),
      ]),
    ),
    ...(partnerScope !== null && definition.filterKeys.includes('partnerId')
      ? { partnerId: partnerScope }
      : {}),
    ...(agentScope !== null && definition.filterKeys.includes('agentId')
      ? { agentId: agentScope }
      : {}),
  } as ReportFilters;
}

function filtersToSearchParams(kind: ReportKind, filters: ReportFilters): URLSearchParams {
  return new URLSearchParams(
    Object.entries(normalizeFiltersForKind(kind, filters)).map(([key, value]) => [
      key,
      String(value),
    ]),
  );
}
function sanitizedFilterValue(kind: ReportKind, field: keyof ReportFilters, value: string): string {
  if (field === 'from' || field === 'to') return validTimestamp(value);
  if (field === 'timezone') return bounded(value, 100) || 'Asia/Bangkok';
  if (field === 'timeBasis') return compatibleReportTimeBasis(kind, value);
  if (field === 'currency')
    return /^[A-Za-z]{3}$/u.test(value.trim()) ? value.trim().toUpperCase() : '';
  if (field === 'sort') return sortSet.has(value) ? value : 'occurredAt';
  if ((uuidFields as readonly string[]).includes(field))
    return uuidPattern.test(value.trim()) ? value.trim() : '';
  if ((stringFields as readonly string[]).includes(field))
    return bounded(value, field === 'username' || field === 'partnerUserId' ? 200 : 100);
  return bounded(value, 200);
}
function validTimestamp(value: string | null): string {
  const trimmed = value?.trim() ?? '';
  return timestampPattern.test(trimmed) && !Number.isNaN(Date.parse(trimmed)) ? trimmed : '';
}
function bounded(value: string | null, max: number): string {
  const trimmed = value?.trim() ?? '';
  return trimmed.length <= max ? trimmed : '';
}
function isReportKind(value: string | undefined): value is ReportKind {
  return value !== undefined && reportKindSet.has(value);
}
function formatScalars(value: Readonly<Record<string, unknown>> | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return (
    Object.entries(value)
      .map(([key, item]) => `${key}: ${item === null ? '—' : String(item)}`)
      .join(' · ') || '—'
  );
}
function safeError(error: unknown, fallback: string): AppError | null {
  if (error === null) return null;
  const requestId =
    typeof error === 'object' &&
    error !== null &&
    'requestId' in error &&
    typeof error.requestId === 'string'
      ? error.requestId
      : null;
  return {
    name: 'AppError',
    message: fallback,
    status: 0,
    code: 'REPORT_ERROR',
    requestId,
    fieldErrors: {},
    retryAfterSeconds: null,
  } as AppError;
}
